import type { DatePickerButton, DatePickerTrackerParams } from '../../types';
import type { NightsPreset, Nights, StartDatePreset } from '@lt/api/dist/types/searchParams';
import type { DateCell } from './types';

type Dates = {
  startDate: Date | null;
  endDate: Date | null;
};

type InputFocuses = {
  isStartDateFocus: boolean;
  isEndDateFocus: boolean;
};

export type VacationOptions = {
  startDatePreset?: StartDatePreset | null;
  nightsPreset?: NightsPreset | null;
  nights?: Nights | null;
};

export type TrackerParams = {
  buttonsClicked: DatePickerButton[];
  initialTab: DatePickerTrackerParams['initial_tab'] | null;
};

export type DatePickerState = VacationOptions &
  Partial<TrackerParams> & {
    left: DateCell;
    right: DateCell;
    shift: number;
    inputFocuses?: InputFocuses;
  };

const initialStateCell: DateCell = {
  date: null,
};

export const initialDatesState = {
  left: initialStateCell,
  right: initialStateCell,
  shift: 0,
  inputFocuses: {
    isStartDateFocus: false,
    isEndDateFocus: false,
  },
};

export const initialVacationOptionsState = {
  startDatePreset: null,
  nightsPreset: null,
  nights: null,
};

export const initialTrackerParamsState = {
  buttonsClicked: [],
  initialTab: null,
};

export const initialState: DatePickerState = {
  ...initialDatesState,
  ...initialVacationOptionsState,
  ...initialTrackerParamsState,
};

const ActionType = {
  DATES: 'changeDates',
  CELLS: 'changeCells',
  CURRENT_CELLS: 'changeCurrentCells',
  SHIFT: 'changeShift',
  SET_START_FOCUS: 'cigarette/setStartFocus',
  SET_END_FOCUS: 'cigarette/setEndFocus',
  SET_START_DATE_PRESET: 'cigarette/startDatePreset',
  SET_NIGHTS_PRESET: 'cigarette/nightsPreset',
  SET_NIGHTS: 'cigarette/nightsInterval',
  UPDATE: 'updateDatePickerState',
  TRACK_BUTTON_CLICK: 'cigarette/clickedButtons',
} as const;

type ActionType = typeof ActionType[keyof typeof ActionType];

export type Action =
  | { type: typeof ActionType.DATES; payload: Dates }
  | {
      type: typeof ActionType.CURRENT_CELLS;
      payload: Pick<DatePickerState, 'left' | 'right'>;
    }
  | {
      type: typeof ActionType.SHIFT;
      payload: number;
    }
  | {
      type: typeof ActionType.SET_START_FOCUS;
    }
  | {
      type: typeof ActionType.SET_END_FOCUS;
    }
  | {
      type: typeof ActionType.SET_START_DATE_PRESET;
      payload: { startDatePreset: StartDatePreset; recommendedNightsPreset?: NightsPreset };
    }
  | { type: typeof ActionType.SET_NIGHTS_PRESET; payload: { nightsPreset: NightsPreset } }
  | { type: typeof ActionType.SET_NIGHTS; payload: { nights: Nights } }
  | {
      type: typeof ActionType.UPDATE;
      payload: DatePickerState;
    }
  | { type: typeof ActionType.TRACK_BUTTON_CLICK; payload: DatePickerButton };

export const reducer = (state: DatePickerState, action: Action): DatePickerState => {
  switch (action.type) {
    case ActionType.DATES: {
      const { startDate, endDate } = action.payload;

      const left = {
        ...state.left,
        date: startDate,
      };
      const right = {
        ...state.right,
        date: endDate,
      };

      return { ...state, left, right, startDatePreset: null, nightsPreset: null, nights: null };
    }
    case ActionType.SHIFT: {
      const shift = action.payload;
      return {
        ...state,
        shift,
      };
    }
    case ActionType.SET_START_FOCUS: {
      return {
        ...state,
        inputFocuses: {
          isStartDateFocus: true,
          isEndDateFocus: false,
        },
      };
    }
    case ActionType.SET_END_FOCUS: {
      return {
        ...state,
        inputFocuses: {
          isStartDateFocus: false,
          isEndDateFocus: true,
        },
      };
    }
    case ActionType.SET_START_DATE_PRESET: {
      const { startDatePreset, recommendedNightsPreset } = action.payload;

      const preset = {
        ...startDatePreset,
        value: {
          from: new Date(startDatePreset.value.from),
          to: new Date(startDatePreset.value.to),
        },
      };

      const nightsDefault =
        !state.nights && !state.nightsPreset ? { nightsPreset: recommendedNightsPreset } : {};

      return {
        ...state,
        ...initialDatesState,
        startDatePreset: preset,
        ...nightsDefault,
      };
    }
    case ActionType.SET_NIGHTS_PRESET: {
      const { nightsPreset } = action.payload;

      return { ...state, right: initialStateCell, nightsPreset, nights: null };
    }
    case ActionType.SET_NIGHTS: {
      const { nights } = action.payload;

      return { ...state, right: initialStateCell, nightsPreset: null, nights };
    }
    case ActionType.UPDATE: {
      return { ...state, ...action.payload };
    }
    case ActionType.TRACK_BUTTON_CLICK: {
      const buttonName = action.payload;
      const clickedButtons = state.buttonsClicked || [];

      const isClickedBefore = clickedButtons.indexOf(buttonName) !== -1;
      if (isClickedBefore) return { ...state };

      return { ...state, buttonsClicked: [...clickedButtons, buttonName] };
    }
    default:
      throw new Error('Incorrect action');
  }
};

export const changeShiftAction = (shift: number) => ({
  type: ActionType.SHIFT,
  payload: shift,
});

export const changeDates = (payload: Dates) => ({
  type: ActionType.DATES,
  payload,
});

export const changeCurrentCells = (payload: Pick<DatePickerState, 'left' | 'right'>) => ({
  type: ActionType.CURRENT_CELLS,
  payload,
});

export const setStartDateFocus = () => ({
  type: ActionType.SET_START_FOCUS,
});

export const setEndDateFocus = () => ({
  type: ActionType.SET_END_FOCUS,
});

export const setStartDatePreset = (payload: {
  startDatePreset: StartDatePreset;
  recommendedNightsPreset: NightsPreset;
}) => ({
  type: ActionType.SET_START_DATE_PRESET,
  payload,
});

export const setNightsPreset = (payload: { nightsPreset: NightsPreset }) => ({
  type: ActionType.SET_NIGHTS_PRESET,
  payload,
});

export const setNights = (payload: { nights: Nights }) => ({
  type: ActionType.SET_NIGHTS,
  payload,
});

export const updateDatepickerState = (payload: DatePickerState) => ({
  type: ActionType.UPDATE,
  payload,
});

export const trackButtonClick = (payload: DatePickerButton) => ({
  type: ActionType.TRACK_BUTTON_CLICK,
  payload,
});
